const styles = '::slotted(input){-webkit-appearance:none;appearance:none;border:0;border-radius:20px;cursor:inherit;display:inline-block;height:3rem;margin:0;opacity:0;padding:0;position:absolute;top:-.75rem;width:3rem;z-index:1}::slotted(input:focus-visible){height:1.5rem;opacity:1;top:0}::slotted(label){cursor:inherit;padding-inline-start:1rem;transition:color .2s ease-in-out}:host{--skapa-switch-active-colour:rgb(var(--colour-interactive-emphasised-bg-default,0,88,163));--skapa-switch-button-colour:rgb(var(--colour-static-white,255,255,255));--skapa-switch-track-colour:rgb(var(--colour-neutral-5,146,146,146));color:var(--skapa-switch-label-colour,rgb(var(--colour-text-and-icon-2,72,72,72)));cursor:pointer;display:inline-flex;font-size:.875rem;line-height:1.571;position:relative}.track{background:border-box var(--skapa-switch-track-colour);border:1px solid #0000;border-radius:20px;flex-shrink:0;height:1.5rem;padding:.0625rem;position:relative;transition:background .2s linear;width:3rem}.button,.track{box-sizing:border-box;display:flex}.button{align-items:center;background:border-box var(--skapa-switch-button-colour);border:2px solid #0000;border-radius:20px;color:var(--skapa-switch-active-colour);flex:0 0 1.25rem;justify-content:center;transition:inherit;transition-property:margin,background,color}.symbol{border:2px solid;border-left:0;border-top:0;box-sizing:initial;display:block;height:.5625rem;margin-top:-.25rem;opacity:0;transform:rotate(45deg);transition:opacity .1s linear;transition-property:opacity,border-color;width:.25rem}:host([checked]) .track{background:var(--skapa-switch-active-colour)}:host([checked]) .button{margin-inline-start:calc(100% - 1.25rem)}:host([checked]) .symbol{opacity:1}:host(:hover){--skapa-switch-active-colour:rgb(var(--colour-interactive-emphasised-bg-hover,0,79,147))}:host(:active){--skapa-switch-active-colour:rgb(var(--colour-interactive-emphasised-bg-pressed,0,62,114))}:host([subtle]){--skapa-switch-button-colour:rgb(var(--colour-neutral-1,255,255,255));--skapa-switch-active-colour:rgb(var(--colour-neutral-7,17,17,17))}:host([subtle]:hover){--skapa-switch-active-colour:rgb(var(--colour-interactive-primary-bg-hover,51,51,51))}:host([subtle]:active){--skapa-switch-active-colour:rgb(var(--colour-interactive-primary-bg-pressed,0,0,0))}:host([disabled]){--skapa-switch-track-colour:rgb(var(--colour-interactive-disabled-1,204,204,204));--skapa-switch-active-colour:rgb(var(--colour-interactive-disabled-1,204,204,204));--skapa-switch-label-colour:rgb(var(--colour-interactive-disabled-1,204,204,204));cursor:default;pointer-events:none}@media (prefers-reduced-motion:reduce){.button,.symbol,.track{transition-duration:0s}}';
const lightStyles = 'skapa-switch input:focus-visible{box-shadow:0 0 0 4px var(--skapa-focus-ring-internal,rgb(var(--colour-neutral-1,255,255,255)));outline:2px solid var(--skapa-focus-ring-external,rgb(var(--colour-neutral-7,17,17,17)));outline-offset:4px;transition:outline .2s ease-out,box-shadow .2s ease-out}';
import { SkapaElement } from "@ingka/skapa-webc-element";
function generateTemplate({ html, prefixReplacer }) {
  const template = document.createElement("template");
  template.innerHTML = html`
    <style>
      ${styles}
    </style>
    <span class="track">
      <span class="button">
        <span class="symbol"></span>
      </span>
    </span>
    <slot></slot>
  `;
  const lightStylesTemplate = document.createElement("style");
  lightStylesTemplate.slot = "light-style";
  lightStylesTemplate.innerHTML = prefixReplacer(lightStyles.toString());
  return { main: template, lightStylesTemplate };
}
class Switch extends SkapaElement {
  constructor() {
    super();
    this.syncAssignedElements = () => {
      if (this.autoID) {
        const idPrefix = this.autoID;
        const label = this.querySelector("label");
        const input = this.querySelector("input");
        if (input) {
          if (!input.hasAttribute("type")) {
            input.setAttribute("type", "checkbox");
            input.setAttribute("role", "switch");
          }
          input.id = idPrefix;
          if (label)
            label.setAttribute("for", input.id);
        }
      }
      this.syncSwitchState();
    };
    this.syncSwitchState = () => {
      const input = this.querySelector("input");
      if (input) {
        input.disabled = this.disabled;
        input.checked = this.checked;
      }
    };
    this.gatherProperties(["disabled", "subtle", "checked", "autoID"], this);
    const shadow = this.shadow = this.attachShadow({ mode: "open" });
    const { main, lightStylesTemplate } = this.getTemplates(generateTemplate);
    shadow.append(main.content.cloneNode(true));
    this.mainSlot = this.shadow.querySelector("slot");
    this.trackElement = this.shadow.querySelector(".track");
    this.lightStyles = lightStylesTemplate.cloneNode(true);
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("change", this.changeHandler);
    this.shadow.addEventListener("slotchange", this.syncAssignedElements);
    this.syncAssignedElements();
    this.append(this.lightStyles);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("change", this.changeHandler);
    this.shadow.removeEventListener("slotchange", this.syncAssignedElements);
  }
  attributeChangedCallback(name) {
    if (name === "auto-id")
      return this.syncAssignedElements();
    this.syncSwitchState();
  }
  changeHandler(ev) {
    this.checked = ev.target.checked;
    this.syncSwitchState();
  }
  get disabled() {
    return this.hasAttribute("disabled");
  }
  set disabled(toggle) {
    this.toggleAttribute("disabled", toggle);
  }
  get subtle() {
    return this.hasAttribute("subtle");
  }
  set subtle(toggle) {
    this.toggleAttribute("subtle", toggle);
  }
  get checked() {
    return this.hasAttribute("checked");
  }
  set checked(checked) {
    this.toggleAttribute("checked", !!checked);
  }
  get autoID() {
    return this.getAttribute("auto-id");
  }
  set autoID(value) {
    if (value)
      this.setAttribute("auto-id", value);
    else
      this.removeAttribute("auto-id");
  }
}
Switch.skapaTagName = "switch";
Switch.skapaDependencies = [];
Switch.observedAttributes = ["disabled", "checked", "auto-id"];
export {
  Switch
};
